import React, { useEffect, useState } from 'react'

import { ChevronDownIcon } from 'lucide-react'

import { Collapse, Popover, Stack, Typography, useTheme } from '@mui/material'

import { useScanRouting } from 'contexts'
import { ReaderSetupPanel } from 'components/display/ReaderSetupPanel/ReaderSetupPanel'
import { ScanSourceRow } from 'components/display/ScanSourceRow/ScanSourceRow'
// Direct file import, NOT the `components/input/filters` barrel — `ScanTargetButton` is reached
// from that barrel's `SearchFilter`, so importing back through it is a cycle. The same cycle
// already threw at runtime once here (`ScanTargetButton is not defined`, see `ActionLine`).
import { filterDropdownPaperSx } from '../filters/dropdownSurface'
import type { ScanSource } from './scanSource'

/**
 * WHICH SCANNER DELIVERS THE NEXT TAP — the operator's choice, made explicitly.
 *
 * ## The defect this replaces
 *
 * Filter scanning decided the source by what happened to be plugged in. A connected desk reader
 * claimed the session silently — no modal, no choice — so an operator whose tags were across the
 * room had no way to say "use my phone" even though the phone works perfectly. With no reader,
 * a modal opened offering "connect a reader" as a PEER of the thing they could actually do:
 * hardware they may not own, presented as an equal option, with the usable path buried under it.
 *
 * Create Certificates already does this correctly — both sources, live status, operator picks.
 * This gives the filter path the same affordance, which it has never had (VRG-265's second pass
 * deleted `SearchByScanModal` and `ScanToSearchModal`, so filter scanning ran entirely through
 * `ScanRoutingContext` + `ScanTargetButton` with the decision implicit inside `aim`).
 *
 * ## A POPOVER, not a modal
 *
 * Create Certificates earns a modal: it is a whole minting run with a product, a quantity, a
 * pending list and a footer action. Aiming a filter field is one decision made in passing, from a
 * 49px button in a toolbar, several times a shift. A modal every time would be heavier than the
 * task it gates — it takes the screen, has to be dismissed, and hides the very filters the scans
 * are about to fill in. Anchored to the icon that opened it, the popover keeps the target in view
 * and costs a click to dismiss by looking away.
 *
 * ## ORDER IS THE MESSAGE: the usable source leads
 *
 * Reader connected → reader first. No reader → phone first, and the reader row drops to the
 * `secondary` treatment with its setup guidance folded behind it. Never lead with an option the
 * user cannot act on. The unusable source is still SHOWN — an operator who owns a reader and has
 * not plugged it in needs to be told that, once — but it cannot outrank the source that works,
 * and clicking it opens setup rather than committing to a scanner that cannot deliver.
 *
 * ## Nothing is marked "selected"
 *
 * The remembered source is expressed by being FIRST, not by a highlight — a highlight would claim
 * a chosen state that survives the click, and these rows close the popover behind them. The §34
 * selectable-card treatment they carry (hover = brand tint only, brand border reserved for
 * "chosen") therefore renders its rest and hover states here and never its selected one. See
 * `scanSourceSurfaceSx`.
 *
 * ## ⚠️ Clicks inside this popover MUST NOT escape it
 *
 * A MUI `Popover` portals its DOM to `document.body`, but React events bubble through the REACT
 * tree, not the DOM tree — and this component is rendered by `ScanTargetButton`, which
 * `SearchFilter` renders INSIDE the field row whose own `onClick` opens the options dropdown.
 * So picking a source also opened the Certificate select's option list behind the QR modal: two
 * stacked surfaces from one click. `ScanTargetButton` already stops its own button and chevron
 * for exactly this reason; the popover's contents were the path nobody had stopped.
 *
 * Stopped at the `Popover` root rather than per row, because the same escape route is open to
 * every descendant — the rows, the reader panel's buttons, and the invisible BACKDROP, whose
 * click-away dismissal would otherwise land on the field beneath as well.
 */

/** Room for a two-word label, a status word and the reader row's chevron without wrapping. */
const PICKER_WIDTH = 340

export interface ScanSourcePickerProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  /**
   * Where scans would land, as it reads mid-sentence — "search", "the Product filter". Supplied
   * by the caller rather than derived here, so the popover and the button it hangs off cannot
   * describe the same destination two different ways.
   */
  destination: string
  /** The operator picked a source. The caller decides what that means for the target. */
  onPick: (source: ScanSource) => void
}

export const ScanSourcePicker = ({
  anchorEl,
  onClose,
  destination,
  onPick,
}: ScanSourcePickerProps) => {
  const theme = useTheme()
  const { readerConnected, phoneConnected } = useScanRouting()
  // Setup guidance the operator asks for, never forced open: `ScanSessionBlock` used to expand it
  // whenever no reader was connected, which spent ~260px on hardware the operator may not own.
  // That was deliberately undone there and must not come back here.
  const [readerHelpOpen, setReaderHelpOpen] = useState(false)

  const open = Boolean(anchorEl)

  // A reader that arrives while the picker is open makes the guidance obsolete mid-read, and the
  // row it belongs to is about to become the leading, pickable one. Collapsing it keeps the
  // popover from re-ordering underneath an expanded panel.
  useEffect(() => {
    if (readerConnected) setReaderHelpOpen(false)
  }, [readerConnected])

  // Reset when it closes rather than when it opens: resetting on open fights the animation, and a
  // reopened picker should not still be showing the guidance from three sessions ago.
  useEffect(() => {
    if (!open) setReaderHelpOpen(false)
  }, [open])

  const pick = (source: ScanSource) => {
    onClose()
    onPick(source)
  }

  const phoneRow = (
    <ScanSourceRow
      key="phone"
      source="phone"
      connected={phoneConnected}
      // NOT "Not connected". A phone is never connected before it has been paired, and reporting
      // that as a fault describes a problem where there is only a next step. The status word here
      // says what the click DOES, which is the one thing the row has to answer.
      status={phoneConnected ? 'Connected' : 'Show QR code'}
      onActivate={() => pick('phone')}
    />
  )

  const readerRowPickable = (
    <ScanSourceRow key="reader" source="reader" connected onActivate={() => pick('reader')} />
  )

  const readerRowSetup = (
    <ScanSourceRow
      key="reader"
      source="reader"
      connected={false}
      secondary
      expanded={readerHelpOpen}
      onActivate={() => setReaderHelpOpen(v => !v)}
      trailing={
        <ChevronDownIcon
          strokeWidth={1}
          size={20}
          style={{
            flexShrink: 0,
            transform: readerHelpOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      }
    >
      <Collapse in={readerHelpOpen}>
        {/* Both of this panel's actions leave the page, and this popover is anchored to a control
            that unmounts with the route — so it is closed first, exactly as `ScanConnectModal`
            does. Without it the popover survives over the destination. */}
        <ReaderSetupPanel connected={false} onNavigate={onClose} />
      </Collapse>
    </ScanSourceRow>
  )

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // The React-tree containment above. `onClick` is enough and `onMouseDown` is not needed:
      // the field row opens on `onClick` alone (`SearchFilter.tsx`, `<Stack onClick={handleClick}>`)
      // — it is not a MUI Select, which would have opened on mousedown and slipped under this.
      // Descendants' own handlers are unaffected; this only stops the climb past the popover.
      onClick={e => e.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          // The house dropdown surface — same radius, same 8px gap under the control, same edge
          // as every filter panel, so this reads as one of them rather than a new kind of thing.
          sx: filterDropdownPaperSx(theme, PICKER_WIDTH),
          // The popover is a labelled group of choices, which is what the trigger's
          // `aria-haspopup="dialog"` promises.
          role: 'dialog',
          'aria-label': 'Choose where scans come from',
        },
      }}
    >
      <Stack spacing={3} sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="body2">Scan with</Typography>
          {/* Names the destination so the choice is not made in the abstract. One sentence, and
              the phrasing comes from the caller — see `destination`. */}
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            The next tag you scan lands in {destination}.
          </Typography>
        </Stack>
        <Stack spacing={2}>
          {readerConnected
            ? [readerRowPickable, phoneRow]
            : [phoneRow, readerRowSetup]}
        </Stack>
      </Stack>
    </Popover>
  )
}

export default ScanSourcePicker
